/*
  AbsorptionIntelligenceCard.jsx (v2, visual-first)
  Generosity, Inc. | WTR App | WTR BTL page

  v2 change from v1: communicates through graphics instead of copy.
  - Basis sentence replaced by a three-gauge signal row (TDS, Score, Temp).
  - Recommendation paragraphs replaced by icon chips that expand on tap.
    Collapsed by default. Tier is shown as a colored dot on the chip and
    as a full badge with source label inside the expanded panel, so the
    per-line tier and source rule still holds.
  - Two sparklines merged into one combined chart: amber bars for TDS,
    blue line for Quality Score, each normalized to its own range, with
    legend chips carrying the averages and deltas.
  - Streak text replaced by a seven-day dot tracker.
  - USPTO phrases remain verbatim and always visible (non-negotiable).

  API contract unchanged: GET /api/bottle/absorption-intelligence
  Dependencies: React only. Inline SVG icons, no icon library.
*/

import React, { useEffect, useMemo, useState } from "react";
import { authHeaders } from "../lib/auth";
import { readProfile } from "../lib/profile";

const COLORS = {
  white: "#FFFFFF",
  blue: "#51B0E6",
  gray: "#A6A8AB",
  light: "#F0F1F3",
  ink: "#232A33",
  amber: "#C7A230",
  amberSoft: "#FBF6E7",
  green: "#2E7D5B",
  greenSoft: "#EAF4EF",
  blueSoft: "#EAF5FC",
};

const TIER_STYLES = {
  verified: { bg: COLORS.greenSoft, fg: COLORS.green, label: "verified" },
  corroborated: { bg: COLORS.blueSoft, fg: COLORS.blue, label: "corroborated" },
  developing: { bg: COLORS.amberSoft, fg: COLORS.amber, label: "developing" },
  hypothesis_only: { bg: COLORS.light, fg: COLORS.gray, label: "hypothesis only" },
};

const USPTO_PHRASES = [
  "Absorption, not just intake.",
  "Absorption efficiency model, not a concentration adjustment model.",
  "Adaptive range, not a fixed threshold.",
];

/* Icon set: 18px inline SVGs keyed by recommendation id. */
const ICONS = {
  filter: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16l-6 8v6l-4 2v-8L4 4z" />
    </svg>
  ),
  temp: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0z" />
    </svg>
  ),
  sipping: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
      <path d="M2 18c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
    </svg>
  ),
  municipal: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  default: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v5M12 16h.01" />
    </svg>
  ),
};

const REC_LABELS = {
  filter: "Filter this source",
  temp: "Chill your fill",
  sipping: "Sip steadily",
  municipal: "Check your source",
};

function TierBadge({ tier }) {
  const t = TIER_STYLES[tier] || TIER_STYLES.hypothesis_only;
  return (
    <span
      style={{
        background: t.bg,
        color: t.fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        padding: "3px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {t.label}
    </span>
  );
}

/*
  Shared display helper: where the marker sits relative to the adaptive
  range. Display logic only, no model logic.
*/
function getRangeStatus(position, band) {
  const pos = Math.max(0, Math.min(1, position != null ? position : 0.5));
  const s = Math.max(0, Math.min(1, band && band.start != null ? band.start : 0.45));
  const e = Math.max(s, Math.min(1, band && band.end != null ? band.end : 0.8));
  if (pos < s) return "below";
  if (pos > e) return "above";
  return "in";
}

/* Dynamic band color for a Quality Score, matching the page's model v2
   scale: 90+ Premium green, 80 Good blue, 70 Moderate gold, 60 Poor red,
   below 60 Very Poor deep red. */
function scoreBandColor(score) {
  if (score == null) return COLORS.gray;
  if (score >= 90) return COLORS.green;
  if (score >= 80) return COLORS.blue;
  if (score >= 70) return COLORS.amber;
  if (score >= 60) return "#B84A4A";
  return "#7A2E28";
}

/* Plain-language TDS band. Users do not think in ppm, so every TDS value
   in the UI travels with a word a human can act on. Unit stays internal. */
function tdsBandWord(tds) {
  if (tds == null) return "";
  if (tds <= 50) return "Very low minerals";
  if (tds <= 150) return "Moderate minerals";
  if (tds <= 300) return "Elevated minerals";
  return "High minerals";
}

/* Ring icon path set, Oura-style: a small icon sits above the value
   inside each ring. Paths are 24-viewBox strokes tinted per ring. */
const RING_ICONS = {
  flask: (
    <path d="M9 3h6M10 3v5l-5 9a3 3 0 0 0 2.6 4.5h8.8A3 3 0 0 0 19 17l-5-9V3" />
  ),
  repeat: (
    <>
      <path d="M17 2l4 4-4 4" />
      <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
      <path d="M7 22l-4-4 4-4" />
      <path d="M21 13v1a4 4 0 0 1-4 4H3" />
    </>
  ),
  drop: <path d="M12 2s-7 8.5-7 13a7 7 0 0 0 14 0c0-4.5-7-13-7-13z" />,
};

/*
  FlowRing v2.6: the same ring language as the Water Quality ring at the
  top of the page and the My Hydration ring, now with an Oura-style icon
  above the value inside the ring. Color and fill are driven live by the
  data, so every ring on the page speaks one visual dialect.
*/
function FlowRing({ label, center, sub, fraction, color, topIcon = null, centerSize = 15 }) {
  const size = 80;
  const r = 31;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const frac = Math.max(0.03, Math.min(1, fraction || 0));
  return (
    <div style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label={`${label}: ${center}`} style={{ display: "block", margin: "0 auto" }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLORS.light} strokeWidth="5.5" />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="5.5"
          strokeLinecap="round"
          strokeDasharray={`${circ * frac} ${circ}`}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: "stroke-dasharray 0.6s ease, stroke 0.4s ease" }}
        />
        {topIcon && RING_ICONS[topIcon] ? (
          <svg
            x={cx - 6}
            y={cy - 21}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {RING_ICONS[topIcon]}
          </svg>
        ) : null}
        <text
          x={cx}
          y={topIcon ? cy + 13 : cy + 6}
          textAnchor="middle"
          fontSize={centerSize}
          fontWeight="700"
          fill={COLORS.ink}
        >
          {center}
        </text>
      </svg>
      <div
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: COLORS.gray,
          marginTop: 5,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>
      {sub ? (
        <div style={{ fontSize: 9.5, color: COLORS.gray, marginTop: 1, whiteSpace: "nowrap" }}>{sub}</div>
      ) : null}
    </div>
  );
}

function FlowChevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLORS.gray} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 32 }}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function DropIcon({ color }) {
  return (
    <svg width="18" height="20" viewBox="0 0 24 26" fill={color}>
      <path d="M12 1C12 1 4 11 4 16.5C4 21.2 7.6 25 12 25C16.4 25 20 21.2 20 16.5C20 11 12 1 12 1Z" />
    </svg>
  );
}

/*
  AdaptiveMeter v2.6: half-moon arc gauge in the Oura dial language.
  Neutral track arc, the user's adaptive range as a soft blue arc zone,
  a progress arc sweeping to the marker in the live status color, tick
  dots along the outside, and the verdict stacked in the arc's hollow:
  droplet, then the status line. Still no fixed color zones on the
  track, so the visual keeps arguing "Adaptive range, not a fixed
  threshold."
*/
function AdaptiveMeter({ position, band }) {
  const pos = Math.max(0, Math.min(1, position != null ? position : 0.5));
  const bandStart = Math.max(0, Math.min(1, band && band.start != null ? band.start : 0.45));
  const bandEnd = Math.max(bandStart, Math.min(1, band && band.end != null ? band.end : 0.8));

  const status = pos < bandStart ? "below" : pos > bandEnd ? "above" : "in";
  const STATUS_TEXT = {
    below: "Below your adaptive range",
    in: "In your adaptive range",
    above: "Above your adaptive range",
  };
  const STATUS_COLOR = { below: COLORS.amber, in: COLORS.blue, above: COLORS.green };
  const markerColor = STATUS_COLOR[status];

  const cx = 100;
  const cy = 100;
  const r = 84;
  const polar = (p, rad) => {
    const a = Math.PI * (1 - Math.max(0, Math.min(1, p)));
    return { x: cx + rad * Math.cos(a), y: cy - rad * Math.sin(a) };
  };
  const arc = (rad, p0, p1) => {
    const s = polar(p0, rad);
    const e = polar(p1, rad);
    return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${rad} ${rad} 0 0 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
  };
  const ticks = [0.25, 0.5, 0.75].filter((t) => Math.abs(t - pos) > 0.07);
  const mk = polar(pos, r);
  const you = polar(pos, 99);

  return (
    <div style={{ marginTop: 16, maxWidth: 280, marginLeft: "auto", marginRight: "auto" }}>
      <div style={{ position: "relative" }}>
        <svg
          viewBox="0 0 200 114"
          style={{ width: "100%", height: "auto", display: "block" }}
          aria-label={`Absorption outlook: ${STATUS_TEXT[status]}`}
        >
          <path d={arc(r, 0, 1)} fill="none" stroke="#ECEEF1" strokeWidth="5" strokeLinecap="round" />
          <path
            d={arc(r, bandStart, bandEnd)}
            fill="none"
            stroke={COLORS.blue}
            strokeOpacity="0.16"
            strokeWidth="12"
            strokeLinecap="round"
          />
          <path d={arc(r, 0.005, pos)} fill="none" stroke={markerColor} strokeWidth="5" strokeLinecap="round" />
          {ticks.map((t) => {
            const p = polar(t, 94);
            return <circle key={t} cx={p.x} cy={p.y} r="1.4" fill="#D3D7DC" />;
          })}
          <circle
            cx={mk.x}
            cy={mk.y}
            r="6"
            fill={COLORS.white}
            stroke={markerColor}
            strokeWidth="3.5"
            style={{ filter: "drop-shadow(0 1px 1.5px rgba(35,42,51,0.3))" }}
          />
          <text
            x={you.x}
            y={you.y}
            textAnchor="middle"
            fontSize="6"
            fontWeight="700"
            letterSpacing="0.12em"
            fill={markerColor}
          >
            YOU
          </text>
          <text x="16" y="112" textAnchor="middle" fontSize="6" letterSpacing="0.14em" fill="#C2C7CD">
            REDUCED
          </text>
          <text x="184" y="112" textAnchor="middle" fontSize="6" letterSpacing="0.14em" fill="#C2C7CD">
            HIGH
          </text>
        </svg>
        {/* Status stack lives in the arc hollow, anchored well above the
            baseline end labels so the two can never collide at any width. */}
        <div style={{ position: "absolute", left: "14%", right: "14%", bottom: "20%", textAlign: "center", pointerEvents: "none" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 2, transform: "scale(0.8)" }}>
            <DropIcon color={markerColor} />
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: markerColor, lineHeight: 1.25 }}>
            {STATUS_TEXT[status]}
          </div>
        </div>
      </div>
      <p style={{ margin: "8px 0 0", fontSize: 10, color: COLORS.gray, textAlign: "center" }}>
        Your range adapts to your recent signals.
      </p>
    </div>
  );
}

/* Icon chip with tap-to-expand detail. Tier dot on the chip, full tier
   badge and source label inside the expanded panel. */
function RecChip({ rec, open, onToggle }) {
  const tier = TIER_STYLES[rec.tier] || TIER_STYLES.hypothesis_only;
  const icon = ICONS[rec.id] || ICONS.default;
  const label = rec.label || REC_LABELS[rec.id] || "Recommendation";
  return (
    <div
      style={{
        background: open ? COLORS.blueSoft : COLORS.light,
        borderRadius: 14,
        marginBottom: 8,
        overflow: "hidden",
        transition: "background 0.15s ease",
      }}
    >
      <button
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          border: "none",
          background: "transparent",
          padding: "10px 12px",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
        }}
      >
        {icon(COLORS.blue)}
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: COLORS.ink }}>{label}</span>
        <span
          title={tier.label}
          style={{ width: 8, height: 8, borderRadius: "50%", background: tier.fg, flexShrink: 0 }}
        />
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke={COLORS.gray}
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s ease", flexShrink: 0 }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div style={{ padding: "0 12px 12px" }}>
          <p style={{ margin: "0 0 6px", fontSize: 12.5, lineHeight: 1.5, color: COLORS.ink }}>{rec.text}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TierBadge tier={rec.tier} />
            <span style={{ fontSize: 11, color: COLORS.gray }}>{rec.source}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/*
  MetricChart v2.2: one metric at a time on a real value scale.
  Clarity devices: value labels on every bar in the 7 day view, a dashed
  average line labeled with the period average, min and max scale labels,
  weekday or date labels on the x axis, and a hollow baseline dot for days
  with no tests so missed days are visible instead of silently absent.
  Fixed aspect viewBox so text never stretches.
*/
function MetricChart({ days, metricKey, color, avg, warnAboveAvg = false, warnColor = null }) {
  const W = 320;
  const H = 150;
  const padL = 10;
  const padR = 10;
  const top = 22;
  const bottom = H - 24;

  const chart = useMemo(() => {
    if (!days || days.length === 0) return null;
    const points = days.map((d) => (d.tests > 0 ? d[metricKey] : null));
    const present = points.filter((v) => v != null);
    if (present.length === 0) return null;
    let min = Math.min.apply(null, present);
    let max = Math.max.apply(null, present);
    if (avg != null) {
      min = Math.min(min, avg);
      max = Math.max(max, avg);
    }
    const pad = (max - min) * 0.12 || 4;
    min -= pad;
    max += pad;
    const span = max - min || 1;
    const y = (v) => bottom - ((v - min) / span) * (bottom - top);
    const n = days.length;
    const slot = (W - padL - padR) / n;
    const barW = Math.min(26, slot * 0.62);
    const bars = days.map((d, i) => {
      const cx = padL + slot * i + slot / 2;
      const v = points[i];
      return { cx, v, y: v != null ? y(v) : null };
    });
    let xLabels;
    if (n <= 7) {
      xLabels = days.map((d, i) => ({
        x: padL + slot * i + slot / 2,
        text: new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "narrow" }),
        tested: d.tests > 0,
      }));
    } else {
      const ticks = [0, Math.round((n - 1) / 4), Math.round((n - 1) / 2), Math.round((3 * (n - 1)) / 4), n - 1];
      xLabels = ticks.map((i) => {
        const dt = new Date(days[i].date + "T00:00:00");
        return {
          x: padL + slot * i + slot / 2,
          text: `${dt.getMonth() + 1}/${dt.getDate()}`,
          tested: true,
        };
      });
    }
    return {
      bars,
      barW,
      xLabels,
      avgY: avg != null ? y(avg) : null,
      minLabel: Math.round(min + pad),
      maxLabel: Math.round(max - pad),
      showValues: n <= 7,
    };
  }, [days, metricKey, avg]);

  if (!chart) {
    return (
      <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", background: COLORS.light, borderRadius: 12 }}>
        <span style={{ fontSize: 11, color: COLORS.gray }}>History builds as you test</span>
      </div>
    );
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} aria-hidden="true">
      <line x1={padL} y1={top} x2={W - padR} y2={top} stroke="#E4E7EA" strokeWidth="1" />
      <line x1={padL} y1={bottom} x2={W - padR} y2={bottom} stroke="#E4E7EA" strokeWidth="1" />
      <text x={padL} y={top - 6} fontSize="8.5" fill={COLORS.gray}>
        {chart.maxLabel}
      </text>
      <text x={padL} y={bottom + 10} fontSize="8.5" fill={COLORS.gray}>
        {chart.minLabel}
      </text>

      {chart.bars.map((b, i) =>
        b.v != null ? (
          <rect
            key={i}
            x={b.cx - chart.barW / 2}
            y={b.y}
            width={chart.barW}
            height={Math.max(2, bottom - b.y)}
            rx="3"
            fill={warnAboveAvg && warnColor && avg != null && b.v > avg ? warnColor : color}
            opacity="0.85"
          />
        ) : (
          <circle key={i} cx={b.cx} cy={bottom - 3} r="2.5" fill="none" stroke={COLORS.gray} strokeWidth="1.2" />
        )
      )}

      {chart.showValues
        ? chart.bars.map((b, i) =>
            b.v != null ? (
              <text key={`v${i}`} x={b.cx} y={b.y - 5} fontSize="9" fontWeight="600" fill={COLORS.ink} textAnchor="middle">
                {Math.round(b.v)}
              </text>
            ) : null
          )
        : null}

      {chart.avgY != null ? (
        <g>
          <line x1={padL} y1={chart.avgY} x2={W - padR} y2={chart.avgY} stroke="#7A828C" strokeWidth="1.2" strokeDasharray="4 3" />
          <text x={W - padR} y={chart.avgY - 4} fontSize="8.5" fontWeight="700" fill="#7A828C" textAnchor="end">
            avg {Math.round(avg)}
          </text>
        </g>
      ) : null}

      {chart.xLabels.map((l, i) => (
        <text
          key={`x${i}`}
          x={l.x}
          y={H - 8}
          fontSize="9"
          fill={l.tested ? COLORS.gray : "#CDD1D6"}
          textAnchor="middle"
        >
          {l.text}
        </text>
      ))}
    </svg>
  );
}

/*
  StatTile v2.2: the averages are now large tappable tiles that double as
  the chart's metric selector. Tap Score or TDS to switch the chart below.
  Each tile carries the period average, its unit, and the delta versus the
  prior period. The selected tile is outlined in the metric's color.
*/
function StatTile({ label, value, suffix, delta, goodWhenNegative, color, selected, onClick }) {
  let deltaEl = <span style={{ fontSize: 11, color: COLORS.gray, fontWeight: 600 }}>steady</span>;
  if (delta != null && !Number.isNaN(delta)) {
    const rounded = Math.round(delta * 10) / 10;
    if (rounded !== 0) {
      const improving = goodWhenNegative ? rounded < 0 : rounded > 0;
      deltaEl = (
        <span style={{ fontSize: 11, fontWeight: 700, color: improving ? COLORS.green : COLORS.amber }}>
          {rounded > 0 ? "\u2191" : "\u2193"} {Math.abs(rounded)} vs prior
        </span>
      );
    }
  }
  return (
    <button
      onClick={onClick}
      aria-pressed={selected}
      style={{
        flex: 1,
        textAlign: "left",
        fontFamily: "inherit",
        cursor: "pointer",
        background: selected ? COLORS.white : COLORS.light,
        border: `2px solid ${selected ? color : "transparent"}`,
        borderRadius: 14,
        padding: "10px 12px",
        boxSizing: "border-box",
        boxShadow: selected ? "0 2px 8px rgba(35,42,51,0.08)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: "inline-block" }} />
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: COLORS.gray }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.ink, lineHeight: 1.1 }}>
        {value}
        {suffix ? <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.gray }}> {suffix}</span> : null}
      </div>
      <div style={{ marginTop: 3 }}>{deltaEl}</div>
    </button>
  );
}

function SectionLabel({ children, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: COLORS.gray }}>
        {children}
      </span>
      {right || null}
    </div>
  );
}

export default function AbsorptionIntelligenceCard({ apiBase = "", liveReading = null }) {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openRec, setOpenRec] = useState(null);
  const [metric, setMetric] = useState("score");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      /* Same identity dialect as the rest of the app: X-WTR-Auth via
         authHeaders() plus the profile email as a query param, matching
         the hydration summary fetch in WTRBottleScreen. */
      const email = ((readProfile().email) || "").trim().toLowerCase();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError("Add your email in your profile to build your absorption history.");
        setLoading(false);
        return;
      }
      try {
        const params = new URLSearchParams({ period, email });
        /* Live-reading params (v1.1, 2026-07-11, empty-bottle incident):
           - levelMl is the submersion evidence stamped on the test entry at
             capture. The server honors a live tds ONLY when levelMl clears
             its gate; a live reading without evidence falls back to stored
             history. Pre-gate entries have no levelMl and correctly get no
             live override.
           - score is NO LONGER SENT. The server always derives the score
             from tds through the one canonical mapping; a client-supplied
             score was an integrity hole (any value could be injected) and
             a step toward exposing the mapping. */
        if (liveReading && liveReading.tdsPpm != null) params.set("tds", String(liveReading.tdsPpm));
        if (liveReading && liveReading.levelMl != null) params.set("levelMl", String(liveReading.levelMl));
        if (liveReading && liveReading.tempF != null) params.set("temp", String(liveReading.tempF));
        const res = await fetch(`${apiBase}/api/bottle/absorption-intelligence?${params.toString()}`, {
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError("Could not load your absorption outlook. Pull to refresh or test again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [apiBase, period, liveReading]);

  const card = {
    background: COLORS.white,
    borderRadius: 20,
    padding: 20,
    boxShadow: "0 2px 12px rgba(35,42,51,0.06)",
    fontFamily: "'Montserrat', 'Helvetica Neue', sans-serif",
    color: COLORS.ink,
  };

  if (loading && !data) {
    return (
      <div style={card} aria-busy="true">
        <SectionLabel>Absorption Intelligence</SectionLabel>
        <div style={{ height: 18, width: "70%", background: COLORS.light, borderRadius: 8, marginBottom: 10 }} />
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, height: 66, background: COLORS.light, borderRadius: 12 }} />
          <div style={{ flex: 1, height: 66, background: COLORS.light, borderRadius: 12 }} />
          <div style={{ flex: 1, height: 66, background: COLORS.light, borderRadius: 12 }} />
        </div>
        <div style={{ height: 26, background: COLORS.light, borderRadius: 999 }} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={card}>
        <SectionLabel>Absorption Intelligence</SectionLabel>
        <p style={{ fontSize: 13, color: COLORS.gray, margin: 0 }}>{error}</p>
      </div>
    );
  }

  const outlook = (data && data.outlook) || {};
  const reading = (data && data.reading) || {};
  const history = (data && data.history) || { days: [], deltas: {} };
  const recs = (data && data.recommendations) || [];
  const streak = (data && data.streakDays) || 0;

  /*
    The causal flow: today's test, the consistency of quality water over
    the period, and the absorption outlook they produce. Consistency
    comes from the server; a display-only fallback computes it from the
    history payload if an older backend is deployed.
  */
  let consistency = data && data.consistency;
  if (!consistency || consistency.pct == null) {
    const tested = history.days.filter((d) => d.tests > 0);
    const quality = tested.filter((d) => d.avgScore != null && d.avgScore >= 70);
    consistency = {
      pct: tested.length > 0 ? quality.length / tested.length : null,
      qualityDays: quality.length,
      testedDays: tested.length,
    };
  }
  const rangeStatus = getRangeStatus(outlook.position, outlook.adaptiveBand);
  const STATUS_COLOR = { below: COLORS.amber, in: COLORS.blue, above: COLORS.green };
  const BAND_WORD = { high: "High", moderate: "Moderate", reduced: "Reduced" };

  return (
    <div style={card}>
      <SectionLabel right={<TierBadge tier={outlook.tier || "developing"} />}>
        Absorption Intelligence
      </SectionLabel>

      <h3 style={{ margin: "2px 0 12px", fontSize: 18, lineHeight: 1.3, fontWeight: 700 }}>
        {outlook.message || "Test your water to see what it means for your body."}
      </h3>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        <FlowRing
          label="Today's Test"
          center={reading.score != null ? Math.round(reading.score) : "--"}
          sub={
            reading.tds != null
              ? `TDS ${Math.round(reading.tds)}${reading.temp != null ? ` \u00B7 ${Math.round(reading.temp)} F` : ""}`
              : null
          }
          fraction={reading.score != null ? reading.score / 100 : 0}
          color={scoreBandColor(reading.score)}
          topIcon="flask"
        />
        <FlowChevron />
        <FlowRing
          label="Consistency"
          center={consistency.pct != null ? `${Math.round(consistency.pct * 100)}%` : "--"}
          sub={
            consistency.testedDays > 0
              ? `${consistency.qualityDays} of ${consistency.testedDays} days`
              : "Test to build"
          }
          fraction={consistency.pct != null ? consistency.pct : 0}
          color={COLORS.blue}
          topIcon="repeat"
        />
        <FlowChevron />
        <FlowRing
          label="Absorption"
          center={outlook.band ? BAND_WORD[outlook.band] : "--"}
          centerSize={9}
          sub={{ below: "Below range", in: "In range", above: "Above range" }[rangeStatus]}
          fraction={outlook.position != null ? outlook.position : 0}
          color={STATUS_COLOR[rangeStatus]}
          topIcon="drop"
        />
      </div>


      <AdaptiveMeter position={outlook.position} band={outlook.adaptiveBand} />

      <div style={{ height: 1, background: COLORS.light, margin: "16px 0 12px" }} />

      <SectionLabel>Improve absorption</SectionLabel>
      {recs.length === 0 ? (
        <p style={{ fontSize: 12.5, color: COLORS.gray, margin: 0 }}>Nothing to fix right now.</p>
      ) : (
        recs.map((r) => (
          <RecChip key={r.id} rec={r} open={openRec === r.id} onToggle={() => setOpenRec(openRec === r.id ? null : r.id)} />
        ))
      )}

      <div style={{ height: 1, background: COLORS.light, margin: "14px 0 12px" }} />

      <SectionLabel
        right={
          <div style={{ display: "flex", gap: 6 }}>
            {["7d", "30d"].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                aria-pressed={period === p}
                style={{
                  border: "none",
                  borderRadius: 999,
                  padding: "4px 12px",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: period === p ? COLORS.blue : COLORS.light,
                  color: period === p ? COLORS.white : COLORS.gray,
                }}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        }
      >
        Your averages
      </SectionLabel>

      {streak > 1 ? (
        <div
          style={{
            display: "inline-block",
            background: COLORS.blueSoft,
            color: COLORS.blue,
            fontSize: 11,
            fontWeight: 700,
            padding: "4px 10px",
            borderRadius: 999,
            marginBottom: 10,
          }}
        >
          Day {streak} test streak
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <StatTile
          label="Quality Score"
          value={history.avgScore != null ? Math.round(history.avgScore) : "--"}
          suffix=""
          delta={history.deltas ? history.deltas.score : null}
          goodWhenNegative={false}
          color={COLORS.blue}
          selected={metric === "score"}
          onClick={() => setMetric("score")}
        />
        <StatTile
          label="TDS"
          value={history.avgTds != null ? Math.round(history.avgTds) : "--"}
          suffix={history.avgTds != null ? tdsBandWord(history.avgTds) : ""}
          delta={history.deltas ? history.deltas.tds : null}
          goodWhenNegative={true}
          color={COLORS.gray}
          selected={metric === "tds"}
          onClick={() => setMetric("tds")}
        />
      </div>

      <MetricChart
        days={history.days}
        metricKey={metric === "score" ? "avgScore" : "avgTds"}
        color={metric === "score" ? COLORS.blue : COLORS.gray}
        avg={metric === "score" ? history.avgScore : history.avgTds}
        warnAboveAvg={metric === "tds"}
        warnColor={COLORS.amber}
      />

      <div style={{ height: 1, background: COLORS.light, margin: "14px 0 10px" }} />

      <div>
        {USPTO_PHRASES.map((p) => (
          <p key={p} style={{ margin: "0 0 4px", fontSize: 10.5, color: COLORS.gray, lineHeight: 1.5, letterSpacing: "0.02em" }}>
            {p}
          </p>
        ))}
      </div>
    </div>
  );
}
